"use client";

import type { AppRouter } from "@klarify/api";
import { useAuth } from "@klarify/auth/client";
import { REPLAY_SESSION_ID_HEADER } from "@klarify/constants";
import { getPostHogSessionReplayUrl } from "@klarify/posthog";
import { logger } from "@klarify/telemetry";
import {
	isBrowserOffline,
	isClerkOfflineError,
	shareInFlight,
} from "@klarify/utilities";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { Operation, TRPCClientError, TRPCLink } from "@trpc/client";
import {
	createTRPCClient,
	httpBatchStreamLink,
	loggerLink,
} from "@trpc/client";
import { type CreateTRPCReact, createTRPCReact } from "@trpc/react-query";
import type { Unsubscribable } from "@trpc/server/observable";
import { observable } from "@trpc/server/observable";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import superjson from "superjson";
import { useRouter } from "../hooks/router";
import { isTrpcErrorResult, makeTrpcLoggerConsole } from "./log-redaction";
import { makeQueryClient } from "./query-client";
import { shouldRetryOperation } from "./retry-policy";
import { toSessionExpiredTRPCError } from "./session-expired-error";
import { getUrl } from "./shared";

export const api: CreateTRPCReact<AppRouter, unknown> =
	createTRPCReact<AppRouter>();

const isDev = process.env.NODE_ENV === "development";

let clientQueryClientSingleton: QueryClient | undefined;
function getQueryClient() {
	if (typeof window === "undefined") {
		// Server: always make a new query client
		return makeQueryClient();
	}
	// Browser: use singleton pattern to keep the same query client
	return (clientQueryClientSingleton ??= makeQueryClient());
}

interface RetryLinkOptions {
	/**
	 * The retry function
	 */
	retry: (opts: RetryFnOptions) => boolean | Promise<boolean>;
	getFinalError?: (opts: RetryFnOptions) => TRPCClientError<AppRouter>;
}

interface RetryFnOptions {
	/**
	 * The operation that failed
	 */
	op: Operation;
	/**
	 * The error that occurred
	 */
	error: TRPCClientError<AppRouter>;
	/**
	 * The number of attempts that have been made (including the first call)
	 */
	attempts: number;
}

function retryLink(opts: RetryLinkOptions): TRPCLink<AppRouter> {
	// initialized config
	return () => {
		// initialized in app
		return (callOpts) => {
			// initialized for request
			return observable((observer) => {
				let next$: Unsubscribable;

				attempt(1);

				function attempt(attempts: number) {
					const op = callOpts.op;

					next$ = callOpts.next(op).subscribe({
						error(error) {
							Promise.resolve(
								opts.retry({
									op,
									attempts,
									error,
								}),
							)
								.then((shouldRetry) => {
									if (shouldRetry) {
										attempt(attempts + 1);
									} else {
										observer.error(
											opts.getFinalError?.({ op, attempts, error }) ?? error,
										);
									}
								})
								.catch(observer.error.bind(observer));
						},
						next(envelope) {
							observer.next(envelope);
						},
						complete() {
							observer.complete();
						},
					});
				}
				return () => {
					next$.unsubscribe();
				};
			});
		};
	};
}

export function TRPCReactProvider(props: { children: React.ReactNode }) {
	const session = useAuth();
	const queryClient = getQueryClient();
	const router = useRouter();
	const lastOrgIdRef = useRef<string | null | undefined>(undefined);

	const revalidateFn = useMemo(() => {
		if (!session.isSignedIn) {
			return undefined;
		}
		// A batch that 401s retries every operation at once; one shared
		// refresh replaces N parallel skipCache token requests (ENG-1898).
		return shareInFlight(async () => {
			// Clerk rejects with ClerkOfflineError before any request while the
			// browser is offline. That carries no signal, so skip the refresh
			// and keep it at debug. Other failures (throttling, transient
			// network errors) stay at warn so real session failures are visible.
			if (isBrowserOffline()) {
				logger.debug("trpc token refresh skipped: browser offline");
				return;
			}
			await session.getToken({ skipCache: true }).catch((err) => {
				if (isClerkOfflineError(err)) {
					logger.debug("trpc token refresh failed: clerk offline");
					return;
				}
				logger.warn("trpc token refresh failed", err);
			});
		});
	}, [session]);

	// Use a ref so the retry closure always sees the latest revalidateFn,
	// even though the trpcClient is only created once via useState.
	const revalidateFnRef = useRef(revalidateFn);
	useEffect(() => {
		revalidateFnRef.current = revalidateFn;
	}, [revalidateFn]);

	useEffect(() => {
		if (!session.isLoaded) {
			return;
		}

		const orgId = session.orgId ?? null;
		const previousOrgId = lastOrgIdRef.current;
		lastOrgIdRef.current = orgId;

		if (previousOrgId === undefined || previousOrgId === orgId) {
			return;
		}

		// tRPC query keys are not scoped by Clerk orgId, and many pages hydrate
		// server component data. Refresh both caches whenever the active clinic
		// changes so stale clinic data cannot remain on screen.
		void (async () => {
			try {
				await queryClient.cancelQueries();
				void queryClient.invalidateQueries().catch(logger.error);
				router.refresh();
			} catch (error) {
				logger.error(error);
			}
		})();
	}, [queryClient, router, session.isLoaded, session.orgId]);

	const [trpcClient] = useState(() =>
		createTRPCClient<AppRouter>({
			links: [
				loggerLink({
					// Dev logs every operation. In production only failures log,
					// redacted — see log-redaction.ts.
					enabled: (opts) =>
						isDev ||
						(opts.direction === "down" && isTrpcErrorResult(opts.result)),
					colorMode: "none",
					console: makeTrpcLoggerConsole(isDev, logger),
				}),
				retryLink({
					async retry(opts) {
						if (!shouldRetryOperation(opts)) {
							return false;
						}
						// Refresh the Clerk session token
						await revalidateFnRef.current?.();
						// Wait briefly for the refreshed JWT to propagate
						await new Promise((resolve) =>
							setTimeout(resolve, 1000 * opts.attempts),
						);
						return true;
					},
					getFinalError(opts) {
						return toSessionExpiredTRPCError(opts.error);
					},
				}),
				httpBatchStreamLink({
					transformer: superjson,
					url: getUrl(),
					fetch(url, options) {
						return fetch(url, {
							...options,
							credentials: "include",
						});
					},
					headers() {
						return {
							[REPLAY_SESSION_ID_HEADER]: getPostHogSessionReplayUrl(),
						};
					},
				}),
			],
		}),
	);

	return (
		<QueryClientProvider client={queryClient}>
			<api.Provider
				client={trpcClient}
				queryClient={queryClient}
			>
				{props.children}
			</api.Provider>
		</QueryClientProvider>
	);
}
